import * as vp from 'vopay';
import { Box, Card, Title, Pagination, usePagination, useUpdateQueryParams } from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import PaymentMethodCard from '../../UI/PaymentMethodCard/PaymentMethodCard';
import BankSearchForm from './BankSearchForm';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import useFetchDefaultPaymentMethod from '@/hooks/useApi/useFetchDefaultPaymentMethod';
import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import usePostSetDefaultPaymentMethod from '@/hooks/useApi/usePostSetDefaultPaymentMethod';
import usePostSetMyClientBankAccount from '@/hooks/useApi/usePostSetMyClientBankAccount';
import usePostSetMyClientCreditCard from '@/hooks/useApi/usePostSetMyClientCreditCard';
import styles from './BankAccount.module.scss';
import ElinxConnect, { EventMessage } from '../../ElinxConnect/ElinxConnect';
import AccountContext, { AccountCtx } from './Dialogue/AccountContext';
import AccountDialogue from './Dialogue/AccountDialogue';
import { AccountDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import InteracDialogue from './InteracDialogue/InteracDialogue';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFetchInteracEmail from '@/hooks/useApi/useFetchInteracEmail';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { MyAccountKeyAndValue } from './types';

import { Button, TableFiltersContainer, Table } from 'UI';

type QueryParams = {
  type: string;
  currency: string;
  searchTerm: string;
};

type AccountBillingSearchParams = {
  type?: string;
  currency?: string;
  searchTerm?: string;
};

type MyAccountTableRow = MyAccountKeyAndValue & { index: number };

type DefaultSave = {
  type: 'bank' | 'credit' | 'vopay';
  token: string;
};
type AccountListType = { [idx: number]: MyAccountKeyAndValue };

type AccountType = {
  AccountListingType?: 'Operational' | 'Billing';
};

type FC = FunctionComponent<AccountType>;

const BankAccountsContainer: FC = ({ AccountListingType = 'Operational' }: AccountType) => {
  const { userSettings } = useUserSettingsProvider();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'Accounts.MyBankAccounts.BankAccountsContainer' });
  const { t: tGlobal } = useTranslation('translations');
  const history = useHistory();
  const { response } = useClientData();
  const { getModuleTitle, onSubNavClick } = useNavMenu();
  const [isShowElinx, setIsShowElinx] = useState(false);
  const [isShowDialogue, setShowDialogue] = useState(false);
  const [defaultPaymentMethodResponse, getDefaultPaymentMethod] = useFetchDefaultPaymentMethod({});
  const [defaultBankAccountResponse, getDefaultBankAccount] = useFetchDefaultBankAccount({});
  const [setDefaultPaymentMethodResponse, setDefaultPaymentMethod] = usePostSetDefaultPaymentMethod({});
  const [setDefautlBankAccountResponse, setDefautlBankAccount] = usePostSetMyClientBankAccount({});
  const [setDefaultCreditCardResponse, setDefaultCreditCard] = usePostSetMyClientCreditCard({});
  const [interacEmailsResponse, fetchInteracEmails] = useFetchInteracEmail({});
  const { permissions } = usePermissions();
  const [filteredLists, setFilteredLists] = useState<AccountListType>({});
  const [accountListsAll, setAccountListsAll] = useState<AccountListType>({});
  const currentLocation = useLocation<{ name: string; type: string; isDelete: boolean } | undefined>();
  const { state } = currentLocation;
  const location = useLocation();

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      type: params.get('type') || '',
      currency: params.get('currency') || '',
      searchTerm: params.get('searchTerm') || '',
    };
  }, [location.search]);

  const type = useMemo(() => queryParams.type || '', [queryParams]);
  const currency = useMemo(() => queryParams.currency || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);

  const filtersApplied = useMemo(() => {
    return Object.values(queryParams).some((val) => val !== '');
  }, [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { setOpenModule } = useOpenModule();

  const { creditCards, fetchCreditCards } = useFetchClientCreditCards();

  const [bankAccountsRes, fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const { setNewCurrencyInfo } = useAccountInfo();
  const [invalidBankAccountImages, setInvalidBankAccountImages] = useState<number[]>([]);
  const [invalidCreditCardImages, setInvalidCreditCardImages] = useState<number[]>([]);

  // pagination
  const totalDataLength = useMemo(() => {
    return Object.values(filteredLists).length;
  }, [filteredLists]);

  const PER_PAGE = 20;

  const { pageCount, pageIndex, offset, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const [showDeleteAccountConfirmAlert, setShowDeleteAccountConfirmAlert] = useState(false);
  const [showDeleteAccountErrorAlert] = useState(false);
  const [showDeleteAccountSuccessAlert] = useState(false);
  const [deleteAccountError] = useState('');
  const [showChangeDefualtBilling, setShowChangeDefualtBilling] = useState(false);
  const [showChangeDefualtOperational, setShowChangeDefualtOperational] = useState(false);
  const [showDeleteBillingConfirmAlert, setShowDeleteBillingConfirmAlert] = useState(false);
  const [showChangeDefualtOperationalSuccessfull, setShowChangeDefualtOperationalSuccessfull] = useState(false);
  const [showChangeDefualtBillingSuccessfull, setShowChangeDefualtBillingSuccessfull] = useState(false);
  const [showAddTypeOfOperational, setShowAddTypeOfOperational] = useState(false);
  const [showAddTypeOfBilling, setShowAddTypeOfBilling] = useState(false);
  const [showAddOperational, setShowAddOperational] = useState(false);
  const [showAddBilling, setShowAddBilling] = useState(false);
  const [showAddOperationalSuccessfull, setShowAddOperationalSuccessfull] = useState(false);
  const [showAddBillingSuccessfull, setShowAddBillingSuccessfull] = useState(false);
  const [accountType, setAccountType] = useState('');
  const [accontNumber, setAccontNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [defaultSave, setDeafualtSave] = useState<DefaultSave>();
  const [currencySelected, setCurrencySelected] = useState('');
  const [saveingDefault, setSavingDefult] = useState(false);
  const [connectedElinx, setConnectedElinx] = useState(false);
  const [tokenTypeBank, setTokenTypeBank] = useState<'bank' | 'credit'>('bank');
  const [paymentMethodOperational, setPaymentMethodOperational] = useState<string>('bankAccount');

  const [isAdding, setIsAdding] = useState(false);
  const [tokenSelectedBank, setTokenSelectedBank] = useState('');
  const [triggerAddOperational, setTriggerAddOperational] = useState(false);
  const [triggerAddBilling, setTriggerAddBilling] = useState(false);
  const [selectableBankAccounts, setSelectableBankAccounts] = useState<MyAccountKeyAndValue[]>([]);
  const [changeDefaultToast, setChangeDefualtToast] = useState(false);

  const DeleteAccount: AccountCtx = {
    showDeleteAccountConfirmAlert,
    showDeleteAccountErrorAlert,
    showDeleteAccountSuccessAlert,
    showDeleteBillingConfirmAlert,
    showChangeDefualtBilling,
    showChangeDefualtOperational,
    deleteAccountError,
    showChangeDefualtOperationalSuccessfull,
    showChangeDefualtBillingSuccessfull,
    showAddTypeOfOperational,
    showAddTypeOfBilling,
    showAddOperational,
    showAddBilling,
    showAddOperationalSuccessfull,
    showAddBillingSuccessfull,
    accontNumber,
    accountType: tGlobal([
      `Accounts.MyBankAccounts.accountTypeItem.${accountType}`,
      'Accounts.MyBankAccounts.accountTypeItem.Bank Account',
    ]),
    accountName,
    currency: currencySelected,
    selectableAccounts: selectableBankAccounts,
    selectionChanged: setTokenSelectedBank,
    token: tokenSelectedBank,
    paymentMehtod: paymentMethodOperational,
    paymentMethodChanged: setPaymentMethodOperational,
  };

  const [elinxConnectSettings, setElinxConnectSettings] = useState<vp.EmbedUrlRequest>({
    RedirectURL: 'https://vopay.com',
    Country: response.Country,
    PaymentSelectionMethod: AccountListingType === 'Operational' ? 'bank,credit' : 'bank,credit',
    ClientControlled: true,
    RedirectMethod: 'JavascriptMessage',
    Language: i18n.language,
  });

  const setAccountDefault = (asDefault: boolean, defaultType?: 'vopay' | 'bank' | 'credit', token2?: string) => {
    if (defaultType === 'bank') {
      // set my bank account
      setDefautlBankAccount({
        PortalUser: response.LoginUserName,
        Token: token2 || '',
        SetAsDefault: asDefault,
        Purpose: AccountListingType.toLowerCase(),
      });
    }

    if (defaultType === 'credit') {
      // credit card set default
      setDefaultCreditCard({
        CreditCardToken: token2 || '',
        SetAsDefault: asDefault,
      });
    }

    if (AccountListingType === 'Billing' && asDefault) {
      setDefaultPaymentMethod({
        PortalUser: response.LoginUserName,
        PaymentMethod: defaultType || 'bank',
      });
    }
  };

  useEffect(() => {
    fetchCreditCards({
      PortalUser: response.LoginUserName,
      ClientControlled: true,
    });
  }, []);

  useEffect(() => {
    if (changeDefaultToast) {
      setChangeDefualtToast(false);
    }
  }, [changeDefaultToast]);

  useEffect(() => {
    if (paymentMethodOperational === 'bankAccount' && AccountListingType === 'Operational') {
      setElinxConnectSettings({
        RedirectURL: 'https://vopay.com',
        Country: response.Country,
        PaymentSelectionMethod: 'bank',
        ClientControlled: true,
        RedirectMethod: 'JavascriptMessage',
        Language: i18n.language,
      });
    }

    if (paymentMethodOperational === 'creditCard' && AccountListingType === 'Operational') {
      setElinxConnectSettings({
        RedirectURL: 'https://vopay.com',
        Country: response.Country,
        PaymentSelectionMethod: 'credit',
        ClientControlled: true,
        RedirectMethod: 'JavascriptMessage',
        Language: i18n.language,
      });
    }
  }, [paymentMethodOperational]);

  useEffect(() => {
    if (state?.isDelete) {
      const newState = { ...state, isDelete: false };

      history.replace({
        ...currentLocation,
        state: newState,
      });
    }
  }, [state]);

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountDialogueHandler = {
        delete: {},
        confirm: {
          confirm: {
            default: () => {
              setShowDeleteAccountConfirmAlert(false);
              setShowDeleteBillingConfirmAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowDeleteAccountConfirmAlert(false);
              setShowDeleteBillingConfirmAlert(false);
            },
          },
        },
        changeDefault: {
          confirm: {
            default: () => {
              setSavingDefult(true);
              setIsAdding(false);
              setAccountDefault(true, defaultSave?.type, defaultSave?.token);
            },
          },
          fail: {
            default: () => {
              if (AccountListingType === 'Operational') {
                setShowChangeDefualtOperational(false);
              } else {
                setShowChangeDefualtBilling(false);
              }
            },
          },
        },
        successDefaultOperational: {
          confirm: {
            default: () => {
              setShowChangeDefualtOperationalSuccessfull(false);
            },
          },
        },
        successDefaultBiliing: {
          confirm: {
            default: () => {
              setShowChangeDefualtBillingSuccessfull(false);
            },
          },
        },
        successAddOperational: {
          confirm: {
            default: () => {
              setShowAddOperationalSuccessfull(false);
            },
          },
        },
        successAddBiliing: {
          confirm: {
            default: () => {
              setShowAddBillingSuccessfull(false);
            },
          },
        },
        operationalType: {
          add: {
            default: () => {
              // code to just add
              setSavingDefult(false);
              setIsAdding(true);
              setAccountDefault(false, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfOperational(false);
            },
          },
          setDefault: {
            default: () => {
              // code to default and add
              setSavingDefult(true);
              setIsAdding(true);
              setAccountDefault(true, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfOperational(false);
            },
          },
          close: {
            default: () => {
              setShowAddTypeOfOperational(false);
            },
          },
        },
        billingType: {
          add: {
            default: () => {
              // code to just add
              setSavingDefult(false);
              setIsAdding(true);
              setAccountDefault(false, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfBilling(false);
            },
          },
          setDefault: {
            default: () => {
              // code to just add
              setSavingDefult(true);
              setIsAdding(true);
              setAccountDefault(true, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfBilling(false);
            },
          },
          close: {
            default: () => {
              setShowAddTypeOfBilling(false);
            },
          },
        },
        addBilling: {
          add: {
            default: () => {
              // set save operational iframe
              setShowAddBilling(false);
              setIsShowElinx(true);
            },
          },
          addAccount: {
            default: () => {
              // code for setting type
              setTriggerAddBilling(true);
            },
          },
          close: {
            default: () => {
              setShowAddBilling(false);
            },
          },
        },
        addOperational: {
          add: {
            default: () => {
              // set save operational or ther on iframe
              setShowAddOperational(false);

              if (paymentMethodOperational === 'interac') {
                setShowDialogue(true);
              } else {
                setIsShowElinx(true);
              }
            },
          },
          addAccount: {
            default: () => {
              // code for setting type
              setTriggerAddOperational(true);
            },
          },
          close: {
            default: () => {
              setPaymentMethodOperational('bankAccount');
              setShowAddOperational(false);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  useEffect(() => {
    if (triggerAddOperational || triggerAddBilling) {
      const account = Object.values(accountListsAll).find(
        (x) => x.Token === tokenSelectedBank || x.CreditCardToken === tokenSelectedBank,
      );

      setCurrencySelected(account?.Currency || 'CAD');
      setAccountType(account?.Type || 'Bank Account');

      setAccontNumber(
        account?.Iban
          ? vp.getMaskedNumber(account.Iban)
          : account?.AccountNumber
            ? vp.getMaskedNumber(account.AccountNumber)
            : account?.CreditCardNumber
              ? vp.getMaskedNumber(account.CreditCardNumber)
              : account?.Type === 'Interac'
                ? account.EmailAddress
                  ? account.EmailAddress
                  : account.RecipientEmailAddress
                    ? account.RecipientEmailAddress
                    : account.Phone
                      ? account.Phone
                      : ''
                : account?.MaskedAccount
                  ? vp.getMaskedNumber(account.MaskedAccount)
                  : account?.CreditCardNumber
                    ? vp.getMaskedNumber(account.CreditCardNumber)
                    : '',
      );

      setAccountName(
        account?.AccountHolderName
          ? account.AccountHolderName
          : account?.RecipientName
            ? account.RecipientName
            : account?.CreditCardHolderName
              ? account.CreditCardHolderName
              : '',
      );

      setDeafualtSave({
        type: account?.Type === 'Credit Card' ? 'credit' : 'bank',
        token: account?.Token || account?.CreditCardToken || '',
      });

      if (tokenSelectedBank !== '') {
        if (triggerAddBilling) {
          setShowAddBilling(false);
          setShowAddTypeOfBilling(true);
        }

        if (triggerAddOperational) {
          setShowAddOperational(false);
          setShowAddTypeOfOperational(true);
        }
      }

      if (triggerAddBilling) {
        setTriggerAddBilling(false);
      }

      if (triggerAddOperational) {
        setTriggerAddOperational(false);
      }
    }
  }, [triggerAddOperational, triggerAddBilling]);

  useEffect(() => {
    refreshBankAccounts();
    refreshEmails();

    getDefaultPaymentMethod({
      PortalUser: response.LoginUserName,
    });

    getDefaultBankAccount({
      PortalUser: response.LoginUserName,
      Timezone: userSettings.timezone,
      ...(currency === 'All' ? {} : { Currency: currency }),
    });
  }, []);

  function refreshEmails() {
    fetchInteracEmails({
      ClientAccountID: '',
      RecipientName: '',
      RecipientEmailAddress: '',
      ClientReferenceNumber: '',
      ClientControlled: true,
    });
  }

  async function refreshBankAccounts() {
    fetchClientBankAccounts({
      PortalUser: response.LoginUserName,
      ClientControlled: true,
      Token: '',
      Timezone: userSettings.timezone,
    });
  }

  useEffect(() => {
    if (bankAccountsRes.Success) {
      // TODO: fetch credit cards on demand (NOT when loading the app)
      const bankAccounts = bankAccountsRes.BankAccounts;

      const myAccounts: MyAccountKeyAndValue[] = Object.values(bankAccounts)
        .filter((account) => account.Purpose?.split(',').includes(AccountListingType))
        .map((bankAccount: vp.ClientBankAccountsLists) => ({
          ...bankAccount,
          Type: 'Bank Account',
          Country: response.Country,
          Province: bankAccount['Province/State'],
          PostalCode: bankAccount['PostalCode/ZipCode'],
          IsDefaultAccount: bankAccount.IsDefault,
          IsDefault:
            (AccountListingType === 'Operational'
              ? Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount).some(
                  (obj) => obj.Token === bankAccount.Token || '',
                )
              : Object.values(defaultBankAccountResponse.DefaultBillingBankAccount).some(
                  (obj) => obj.Token === bankAccount.Token,
                )) &&
            (AccountListingType === 'Operational' || defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank')
              ? '1'
              : '0',
          IsOperationalDefault: Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount).some(
            (obj) => obj.Token === bankAccount.Token || '',
          )
            ? '1'
            : '0',
          IsBillingDefault: Object.values(defaultBankAccountResponse.DefaultBillingBankAccount).some(
            (obj) => obj.Token === bankAccount.Token,
          )
            ? '1'
            : '0',
        }));

      const myAccountsOther: MyAccountKeyAndValue[] = Object.values(bankAccounts)
        .filter((account) => !account.Purpose?.split(',').includes(AccountListingType))
        .map((bankAccount: vp.ClientBankAccountsLists) => ({
          ...bankAccount,
          Type: 'Bank Account',
          Country: response.Country,
        }));

      const myCards: MyAccountKeyAndValue[] = creditCards.map((creditCard) => ({
        ...creditCard,
        Type: 'Credit Card',
        Country: response.Country,
        IsDefault:
          defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit' &&
          AccountListingType === 'Billing' &&
          creditCard.IsDefault === '1'
            ? '1'
            : '0',
        IsOperationalDefault: '0',
        IsBillingDefault:
          defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit' && creditCard.IsDefault === '1' ? '1' : '0',
        IsDefaultAccount: creditCard.IsDefault,
        Currency: 'CAD',
      }));

      const myInteracs: MyAccountKeyAndValue[] = Object.values(
        AccountListingType === 'Operational' ? interacEmailsResponse.InteracEmails || {} : {},
      ).map((creditCard) => ({
        ...creditCard,
        Type: 'Interac',
        Country: response.Country,
        Currency: 'CAD',
        IsDefault: '0',
        IsDefaultAccount: '0',
        IsOperationalDefault: '0',
        IsBillingDefault: '0',
      }));

      const mergedContacts: AccountListType = {};

      myAccounts
        .concat(myCards)
        .concat(myInteracs)
        .forEach((contacts, index) => {
          mergedContacts[index] = { ...contacts };
        });

      const mergedContacts2: AccountListType = {};

      myAccountsOther
        .concat(myCards)
        .concat(myInteracs)
        .forEach((contacts, index) => {
          mergedContacts2[index] = { ...contacts };
        });

      const mergedContacts3: AccountListType = {};

      myAccountsOther
        .concat(myCards)
        .concat(myAccounts)
        .concat(myInteracs)
        .forEach((contacts, index) => {
          mergedContacts3[index] = { ...contacts };
        });

      if (connectedElinx) {
        const selectedAccount = Object.values(mergedContacts3).find(
          (x) => x.Token === tokenSelectedBank || x.CreditCardToken === tokenSelectedBank,
        );

        if (selectedAccount) {
          if (AccountListingType === 'Billing') {
            setTriggerAddBilling(true);
          } else if (selectedAccount.Type === 'Credit Card') {
            setAccountType(selectedAccount.Type);
            setAccountName(selectedAccount.CreditCardHolderName || '');

            setTriggerAddOperational(true);
          } else {
            setTriggerAddOperational(true);
          }

          setConnectedElinx(false);
        }
      }

      setSelectableBankAccounts(
        Object.values(mergedContacts2).filter(
          (x) => !x.Purpose?.split(',').includes(AccountListingType) && x.Type === 'Bank Account',
        ),
      );

      const searchParams = {
        type,
        currency,
        searchTerm,
      };

      const keys = ['AccountHolderName', 'CreditCardHolderName', 'FirstName', 'LastName', 'FullName', 'RecipientName'];

      setFilteredLists(
        Object.values(mergedContacts || {}).filter(
          (data) =>
            (searchParams.type === '' ||
              searchParams.type === 'All' ||
              data.Type.replace(/\s+/g, '') === searchParams.type) &&
            (searchParams.searchTerm === '' ||
              keys.some((key) =>
                data[key as keyof MyAccountKeyAndValue]
                  ?.toLowerCase()
                  .includes(searchParams.searchTerm || ''?.toLowerCase()),
              )),
        ),
      );

      setAccountListsAll(mergedContacts3);
    }
  }, [
    creditCards,
    interacEmailsResponse,
    defaultPaymentMethodResponse,
    defaultBankAccountResponse,
    bankAccountsRes,
    bankAccountsRes.BankAccounts,
    connectedElinx,
    AccountListingType,
    response.Country,
    tokenSelectedBank,
    type,
    currency,
    searchTerm,
  ]);

  useEffect(() => {
    getDefaultPaymentMethod({
      PortalUser: response.LoginUserName,
    });
  }, [setDefaultPaymentMethodResponse]);

  // TODO ────────────────────────────────────────────────────────────────────
  // 1. Surface set-as-default failures to the user — currently silent.
  //    This effect and the credit-card one below only handle
  //    Success: true. On Success: false nothing happens — no error surfaced,
  //    confirm dialogue stays open. Surface the backend ErrorMessage and
  //    close/keep the dialogue deliberately (applies to all four call sites:
  //    bank/credit × Billing/Operational).
  // 2. Dead state: changeDefaultToast is set true here and immediately reset
  //    false by the effect near the top of this component; nothing renders it.
  //    Delete it or wire an actual success toast.
  // 3. Stuck fullscreen loader after an in-page Search: search() fires both
  //    fetchClientBankAccounts and getDefaultBankAccount; the second request
  //    is raced by its own legacy hook's CancelToken cleanup and, when
  //    cancelled, the hook's awaited call rejects after it has already set the
  //    fullscreen indicator on — setDataFetchingInfo(false) never runs and the
  //    loader modal blocks the page until reload. Migrating these hooks to the
  //    Direct Pattern (try/finally around the indicator) fixes it. The Cypress
  //    scenario "Switching tabs clears the payment method filters" deep-links
  //    its filter to avoid this; restore its search→tab-switch variant then.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (setDefautlBankAccountResponse.Success) {
      refreshBankAccounts();
      setShowChangeDefualtBilling(false);
      setShowChangeDefualtOperational(false);
      setShowAddTypeOfBilling(false);
      setShowAddTypeOfOperational(false);

      if (!saveingDefault) {
        if (AccountListingType === 'Operational') {
          setShowAddOperationalSuccessfull(true);
        } else {
          setShowAddBillingSuccessfull(true);
        }
      } else if (isAdding) {
        if (AccountListingType === 'Operational') {
          setShowChangeDefualtOperationalSuccessfull(true);
        } else {
          setShowChangeDefualtBillingSuccessfull(true);
        }
      } else if (AccountListingType === 'Operational') {
        setChangeDefualtToast(true);
      } else {
        setChangeDefualtToast(true);
      }

      getDefaultBankAccount({
        PortalUser: response.LoginUserName,
        Timezone: userSettings.timezone,
        ...(currency === 'All' ? {} : { Currency: currency }),
      });
    }
  }, [setDefautlBankAccountResponse]);

  useEffect(() => {
    if (setDefaultCreditCardResponse.Success) {
      setShowChangeDefualtBilling(false);
      setShowChangeDefualtOperational(false);
      setShowAddTypeOfBilling(false);
      setShowAddTypeOfOperational(false);

      if (!saveingDefault) {
        if (AccountListingType === 'Operational') {
          setShowAddOperationalSuccessfull(true);
        } else {
          setShowAddBillingSuccessfull(true);
        }
      } else if (isAdding) {
        if (AccountListingType === 'Operational') {
          setShowChangeDefualtOperationalSuccessfull(true);
        } else {
          setShowChangeDefualtBillingSuccessfull(true);
        }
      } else if (AccountListingType === 'Operational') {
        setChangeDefualtToast(true);
      } else {
        setChangeDefualtToast(true);
      }

      getDefaultBankAccount({
        PortalUser: response.LoginUserName,
        Timezone: userSettings.timezone,
        ...(currency === 'All' ? {} : { Currency: currency }),
      });
    }
  }, [setDefaultCreditCardResponse]);

  const search = (searchParams: AccountBillingSearchParams = {}) => {
    const defaultValues = {
      PortalUser: response.LoginUserName,
      ClientControlled: true,
      Token: '',
      ...(searchParams.currency === 'All' ? {} : { Currency: searchParams.currency }),
      SearchText: searchParams.searchTerm,
    };

    fetchClientBankAccounts(defaultValues);

    getDefaultBankAccount({
      PortalUser: response.LoginUserName,
      ...(searchParams.currency === 'All' ? {} : { Currency: searchParams.currency }),
    });
  };

  useEffect(() => {
    setPageIndex(0);

    const searchParams = {
      type,
      currency,
      searchTerm,
    };

    updateQueryParams(searchParams);
    search(searchParams);
  }, []);

  const handleBankAccountImageError = (rowIndex: number) => {
    setInvalidBankAccountImages((prevInvalidImages) => [...prevInvalidImages, rowIndex]);
  };

  const handleCreditCardImageError = (rowIndex: number) => {
    setInvalidCreditCardImages((prevInvalidImages) => [...prevInvalidImages, rowIndex]);
  };

  const tableProps: vp.TableProps<MyAccountTableRow> = {
    id: 'my-bank-account-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'account',
        header: t('table.account') || '',
        align: 'left',
        template: ({ data }) => (
          <Box flx="col">
            <Box flx="row" ai="center">
              {data.BankId !== null &&
                !invalidBankAccountImages.includes(data.index) &&
                data.Type === 'Bank Account' && (
                  <img
                    alt="logo"
                    onError={() => handleBankAccountImageError(data.index)}
                    src={`https://embed-dev.vopay.com/images/logos-banks/${data.BankId}.svg`}
                  />
                )}

              {data.CreditCardBrand !== null &&
                !invalidCreditCardImages.includes(data.index) &&
                data.Type === 'Credit Card' && (
                  <img
                    alt="logo"
                    onError={() => handleCreditCardImageError(data.index)}
                    src={`https://embed-dev.vopay.com/images/${data.CreditCardBrand}.png`}
                  />
                )}

              <Table.Link
                to={{
                  pathname: `/${
                    AccountListingType === 'Operational' ? 'operational-accounts' : 'account-and-billing'
                  }/details/${data.Key || 0}/${data.Type.replace(/\s+/g, '')}/${
                    data.Token || data.CreditCardToken || data.ID
                  }?${queryString.stringify(data)}`,
                  state: { account: data },
                }}
              >
                {data.Iban
                  ? vp.getMaskedNumber(data.Iban)
                  : data.AccountNumber
                    ? vp.getMaskedNumber(data.AccountNumber)
                    : data.CreditCardNumber
                      ? vp.getMaskedNumber(data.CreditCardNumber)
                      : data.Type === 'Interac'
                        ? data.EmailAddress || data.RecipientEmailAddress || data.Phone || ''
                        : data.MaskedAccount
                          ? vp.getMaskedNumber(data.MaskedAccount)
                          : ''}
              </Table.Link>
            </Box>
          </Box>
        ),
      },
      {
        id: 'clientName',
        header: t('table.accountHolder') || '',
        align: 'left',
        template: ({ data }) => data.AccountHolderName || data.RecipientName || data.CreditCardHolderName || '',
      },
      {
        id: 'type',
        header: t('table.accountType') || '',
        align: 'left',
        template: ({ data }) =>
          data.Type
            ? tGlobal(`Accounts.MyBankAccounts.accountTypeItem.${data.Type}`)
            : tGlobal('Accounts.MyBankAccounts.accountTypeItem.Bank Account'),
      },
      {
        id: 'currency',
        header: t('table.currency') || '',
        align: 'left',
        field: 'Currency',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => {
          const isDefault =
            ((AccountListingType === 'Operational'
              ? Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount).some(
                  (obj) => obj.Token === data.Token,
                )
              : Object.values(defaultBankAccountResponse.DefaultBillingBankAccount).some(
                  (obj) => obj.Token === data.Token,
                )) &&
              (AccountListingType === 'Operational' || defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank')) ||
            (data.Type === 'Credit Card' &&
              data.IsDefault === '1' &&
              AccountListingType === 'Billing' &&
              defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit');

          return (
            <Box gap="sm" jc="fe">
              {!isDefault &&
                (data.IsDefaultAccount !== '1' ||
                  data.IsBillingDefault === '1' ||
                  data.IsOperationalDefault === '1' ||
                  data.Type !== 'Bank Account') &&
                (data.Type === 'Bank Account' || (data.Type === 'Credit Card' && AccountListingType === 'Billing')) && (
                  <Button
                    id={`account-detail-${data.index}`}
                    size="small"
                    disabled={
                      (AccountListingType === 'Billing' && !permissions.ManageAccountandBilling) ||
                      (AccountListingType === 'Operational' && !permissions.ManageDefaultPayments)
                    }
                    onClick={() => {
                      const masked = data.Iban
                        ? vp.getMaskedNumber(data.Iban)
                        : data.AccountNumber
                          ? vp.getMaskedNumber(data.AccountNumber)
                          : data.CreditCardNumber
                            ? vp.getMaskedNumber(data.CreditCardNumber)
                            : data.Type === 'Interac'
                              ? data.EmailAddress || data.RecipientEmailAddress || data.Phone || ''
                              : data.MaskedAccount
                                ? vp.getMaskedNumber(data.MaskedAccount)
                                : '';

                      setCurrencySelected(data.Currency);
                      setAccountType(data.Type);
                      setAccontNumber(masked);
                      setAccountName(data.AccountHolderName || data.RecipientName || data.CreditCardHolderName || '');

                      if (AccountListingType === 'Operational') {
                        setShowChangeDefualtOperational(true);
                      } else {
                        setShowChangeDefualtBilling(true);
                      }

                      setDeafualtSave({
                        type: data.Type === 'Credit Card' ? 'credit' : 'bank',
                        token: data.Token || data.CreditCardToken || '',
                      });
                    }}
                  >
                    {AccountListingType === 'Operational'
                      ? t('table.setAsDefaultOperational')
                      : t('table.setAsDefaultBilling')}
                  </Button>
                )}
            </Box>
          );
        },
      },
    ],
    rows: Object.values(filteredLists || {})
      .sort((a, b) => (a.IsDefault === b.IsDefault ? 0 : vp.toBool(a.IsDefault) ? -1 : 1))
      .map((item, index) => ({
        id: item.Token || item.CreditCardToken || item.ID || String(Math.random()),
        data: { ...item, index },
      }))
      .slice(offset, offset + PER_PAGE),
  };

  const defaultAccountCardBanks = Object.values(
    AccountListingType === 'Operational'
      ? defaultBankAccountResponse.DefaultOperationalBankAccount || {}
      : defaultBankAccountResponse.DefaultBillingBankAccount || {},
  )
    .sort((payment1, payment2) => payment1.Currency.localeCompare(payment2.Currency))
    .map((payment) => (
      <PaymentMethodCard
        Title={
          AccountListingType === 'Operational'
            ? t('card.operationalDefault', { currency: payment.Currency })
            : t('card.billingDefault', { currency: payment.Currency })
        }
        Description={
          AccountListingType === 'Operational'
            ? t('card.operationalDescription', {
                currency: payment.Currency,
                additionalText: response.SubAccountType !== 'franchise' ? t('card.andWithdrawals') : '',
              })
            : t('card.billingDescription', { currency: payment.Currency })
        }
        AccountNumber={payment.AccountNumber}
        AccountHolder={payment.AccountHolderName}
        ConnectionType={payment.Type === 'manual' ? 'manual' : 'online'}
        Expiry=""
        Type="bank"
        BankId={payment.BankId}
        CreditCardBrand=""
        onClickFund={
          AccountListingType === 'Operational'
            ? () => {
                setOpenModule('myAccount');
                onSubNavClick('fundMyAccount', '/fund-my-account', 'Fund My Account');
                setNewCurrencyInfo(payment.Currency);

                history.push({
                  pathname: '/fund-my-account',
                  state: {
                    token: payment.Token,
                    type: 'Bank Account',
                    currency: payment.Currency,
                  },
                });
              }
            : undefined
        }
        onClickWithdraw={
          AccountListingType === 'Operational'
            ? () => {
                setOpenModule('myAccount');
                onSubNavClick('withdrawMyAccount', '/withdraw-my-account', 'Fund My Account');
                setNewCurrencyInfo(payment.Currency);

                history.push({
                  pathname: '/withdraw-my-account',
                  state: {
                    token: payment.Token,
                    type: 'Bank Account',
                    currency: payment.Currency,
                  },
                });
              }
            : undefined
        }
      />
    ));

  const defaultAccountCardCreditCard = creditCards
    .filter((x) => x.IsDefault === '1')
    .map((payment) => (
      <PaymentMethodCard
        Title={AccountListingType === 'Operational' ? t('card.cadOperationalDefault') : t('card.billingDefault')}
        Description={
          AccountListingType === 'Operational' ? t('card.cadOperationalDescription') : t('card.billingDescription')
        }
        AccountNumber={vp.getMaskedNumber(payment.CreditCardNumber)}
        AccountHolder={payment.CreditCardHolderName}
        ConnectionType="online"
        Expiry={`20${payment.CreditCardExpiryYear}${'/'}${payment.CreditCardExpiryMonth}`}
        Type="credit"
        BankId=""
        CreditCardBrand={payment.CreditCardBrand}
      />
    ));

  const onClosePaymentMethod = () => {
    setIsShowElinx(false);
  };

  const onConnectPaymentMethod = (eventValues: EventMessage) => {
    setIsShowElinx(false);
    setTokenSelectedBank(eventValues.data.Token);

    if (eventValues.data.PaymentMethod === 'creditcard') {
      setTokenTypeBank('credit');
    } else if (eventValues.data.PaymentMethod === 'bank') {
      setTokenTypeBank('bank');
    }

    if (eventValues.data.PaymentMethod === 'creditcard' && AccountListingType === 'Operational') {
      setConnectedElinx(true);

      fetchCreditCards({
        PortalUser: response.LoginUserName,
        ClientControlled: true,
      });
    } else {
      setConnectedElinx(true);

      fetchCreditCards({
        PortalUser: response.LoginUserName,
        ClientControlled: true,
      });

      refreshBankAccounts();
      refreshEmails();
    }
  };

  const onCloseInterac = () => {
    setPaymentMethodOperational('bankAccount');
    setShowDialogue(false);
  };

  const onSuccessInterac = (name: string) => {
    setPaymentMethodOperational('bankAccount');
    setAccountName(name);
    setAccountType('Interac');
    setShowAddOperationalSuccessfull(true);
    setShowDialogue(false);
    refreshBankAccounts();
  };

  const MemoizedElinxConnect = useMemo(
    () => (
      <ElinxConnect setting={elinxConnectSettings} onSuccess={onConnectPaymentMethod} onClose={onClosePaymentMethod} />
    ),
    [elinxConnectSettings],
  );

  return (
    <>
      <AccountContext.Provider value={DeleteAccount}>
        <Box jc="sb" flx="row">
          {AccountListingType === 'Operational' && (
            <Box flx="col" mb="md">
              <Box mb="md" jc="sb">
                <Title title={getModuleTitle()} />
              </Box>

              <Box>
                {t('description.operationalAccounts', {
                  additionalText: response.SubAccountType !== 'franchise' ? t('description.withdrawFromAnd') : '',
                })}
              </Box>
            </Box>
          )}

          {AccountListingType === 'Billing' && (
            <div className={styles.billingContainer}>
              <Box height="auto">
                {defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank' && defaultAccountCardBanks}
                {defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit' && defaultAccountCardCreditCard}
              </Box>

              <Box jc="fe">
                <Button
                  id="contacts-addBank"
                  colorScheme="primary"
                  disabled={!permissions.ManageAccountandBilling}
                  onClick={() => {
                    if (vp.toBool(response.ClientAccountsEnabled)) {
                      setIsShowElinx(true);
                    } else {
                      setShowAddBilling(true);
                    }
                  }}
                >
                  {t('button.addBillingMethod')}
                </Button>
              </Box>
            </div>
          )}
        </Box>

        {AccountListingType === 'Operational' ? (
          <Card style={{ padding: '16px' }}>
            <Box jc="sb" flx="row">
              <Box height="auto">
                {defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank' && defaultAccountCardBanks}
              </Box>

              {
                <Box mb="md" jc="sb">
                  <Button
                    id="my-bank-accounts"
                    colorScheme="primary"
                    disabled={!permissions.ManageDefaultPayments}
                    onClick={() => {
                      setShowAddOperational(true);
                    }}
                  >
                    {t('button.addOperationalAccount')}
                  </Button>
                </Box>
              }
            </Box>

            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <div style={{ marginTop: '16px', marginLeft: 'auto' }}>
                  <TableFiltersContainer.Trigger />
                </div>

                <TableFiltersContainer.Content>
                  <BankSearchForm
                    type={type}
                    currency={currency}
                    searchTerm={searchTerm}
                    accountListingType={AccountListingType}
                    search={(searchParams) => {
                      setPageIndex(0);

                      updateQueryParams({
                        type: searchParams.type || '',
                        currency: searchParams.currency || '',
                        searchTerm: searchParams.searchTerm || '',
                      });

                      search(searchParams);
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>

            <Box>
              <div className={styles.bankAccountTable}>
                <Table {...tableProps} />
              </div>
            </Box>

            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    setPageIndex(e.selected);
                  }}
                />
              )}
            </Box>
          </Card>
        ) : (
          <>
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <div style={{ marginTop: '16px', marginLeft: 'auto' }}>
                  <TableFiltersContainer.Trigger />
                </div>

                <TableFiltersContainer.Content>
                  <BankSearchForm
                    type={type}
                    currency={currency}
                    searchTerm={searchTerm}
                    accountListingType={AccountListingType}
                    search={(searchParams) => {
                      setPageIndex(0);

                      updateQueryParams({
                        type: searchParams.type || '',
                        currency: searchParams.currency || '',
                        searchTerm: searchParams.searchTerm || '',
                      });

                      search(searchParams);
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>

            <Box>
              <div className={styles.bankAccountTable}>
                <Table {...tableProps} />
              </div>
            </Box>

            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    setPageIndex(e.selected);
                  }}
                />
              )}
            </Box>
          </>
        )}

        {isShowElinx && MemoizedElinxConnect}
        <InteracDialogue isVisible={isShowDialogue} onClose={onCloseInterac} onSuccess={onSuccessInterac} />

        {createPortal(
          <AccountDialogue handleAccountDialogue={handlers.handleAccountDialogue} />,
          document.getElementById('modal-root')!,
        )}
      </AccountContext.Provider>
    </>
  );
};

export default BankAccountsContainer;
